import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import RentalService from '../../services/RentalService';
import { Rental, RentalStatus } from '../../services/models/rental';
import useStation from '../../hooks/useStation';

import RentalLockTimeline from './components/RentalLockTimeline';
import RentalHistoryList from './components/RentalHistoryList';
import RentalDetailsModal from './components/RentalDetailsModal';
import StationAnalytics from './components/StationAnalytics';

const HISTORY_PAGE_SIZE = 20;

interface HistoryStatusFilter {
  label: string;
  invoiceStatus?: string;
  rentalStatus?: RentalStatus;
}

const HISTORY_STATUS_FILTERS: HistoryStatusFilter[] = [
  { label: 'Tous' },
  { label: 'À vérifier', rentalStatus: RentalStatus.RETURNED },
  { label: 'Payé', invoiceStatus: 'PAID' },
  { label: 'Annulé', invoiceStatus: 'CANCELED' },
  { label: 'Échoué', invoiceStatus: 'FAILED' },
];

type RentalTab = 'current' | 'history' | 'stats';

const StationRentals = () => {
  const { siteId, stationId } = useParams<{ siteId: string; stationId: string }>();
  const numericStationId = Number(stationId);

  const { station } = useStation(siteId, stationId);

  const [tab, setTab] = useState<RentalTab>('current');
  const [historyPage, setHistoryPage] = useState(0);
  const [historyFilter, setHistoryFilter] = useState<HistoryStatusFilter>(HISTORY_STATUS_FILTERS[0]);
  const [selectedRental, setSelectedRental] = useState<Rental | null>(null);

  const onHistoryFilterChange = (filter: HistoryStatusFilter) => {
    setHistoryFilter(filter);
    setHistoryPage(0);
  };

  // History rentals (list) — server-side filtered by invoice or rental status when selected
  const { data: historyData, isFetching: isHistoryFetching } = useQuery({
    queryKey: ['rentals', numericStationId, 'history', historyPage, historyFilter.label],
    queryFn: () => RentalService.getRentalsHistory(numericStationId, historyPage, HISTORY_PAGE_SIZE, {
      invoiceStatus: historyFilter.invoiceStatus,
      rentalStatus: historyFilter.rentalStatus,
    }),
    enabled: !!stationId && tab === 'history',
  });

  const tabs: { key: RentalTab; label: string }[] = [
    { key: 'current', label: 'En cours' },
    { key: 'history', label: 'Historique' },
    { key: 'stats', label: 'Statistiques' },
  ];

  return (
    <div className="w-full max-w-[1280px] px-4 flex flex-col gap-6 pb-8">
      {/* Header */}
      <div className="flex flex-col gap-1">
        <h1 className="typo-h1 text-neutral">Réservations</h1>
        <p className="typo-small text-neutral-40">
          {station?.name || `Station ${numericStationId}`}
        </p>
      </div>

      {/* Tabs (left) + history invoice-status filter (right) on one row */}
      <div className="self-stretch flex items-center justify-between gap-4 flex-wrap">
        <div className="inline-flex items-center gap-1 p-1 rounded-lg bg-neutral-5">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`px-4 py-1.5 rounded-md typo-small font-medium transition-colors ${
                tab === t.key
                  ? 'bg-white text-neutral shadow-sm'
                  : 'text-neutral-50 hover:text-neutral'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'history' && (
          <div className="inline-flex items-center gap-1 p-1 rounded-lg bg-neutral-5">
            {HISTORY_STATUS_FILTERS.map((f) => (
              <button
                key={f.label}
                type="button"
                onClick={() => onHistoryFilterChange(f)}
                className={`px-3 py-1.5 rounded-md typo-small font-medium transition-colors ${
                  historyFilter.label === f.label ? 'bg-white text-neutral shadow-sm' : 'text-neutral-50 hover:text-neutral'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {tab === 'current' && (
        <RentalLockTimeline
          stationId={numericStationId}
          locks={(station?.ecus ?? [])
            .slice()
            .sort((a, b) => a.address - b.address)
            .flatMap((ecu) => ecu.locks ?? [])}
          onRentalClick={setSelectedRental}
        />
      )}
      {tab === 'history' && (
        <RentalHistoryList
          rentals={historyData?.rentals ?? []}
          page={historyPage}
          totalPages={historyData?.metadata?.totalPages}
          isLoading={isHistoryFetching}
          onPageChange={setHistoryPage}
          onRentalClick={setSelectedRental}
        />
      )}
      {tab === 'stats' && <StationAnalytics stationId={numericStationId} />}

      <RentalDetailsModal
        rental={selectedRental ?? undefined}
        stationToken={station?.token}
        stationId={numericStationId}
        discountPercentage={station?.discountPercentage}
        visible={!!selectedRental}
        onClose={() => setSelectedRental(null)}
        onRentalUpdated={setSelectedRental}
      />
    </div>
  );
};

export default StationRentals;
